<script lang="ts" setup>
import type { BonusWalletData } from '@/interfaces/IBonusWallet'
import { amountHelper } from '@nami/domain/classes'
import { storeToRefs } from 'pinia'
import { computed } from 'vue'
import IconBonusWallet from '@/components/icons/IconBonusWallet.vue'
import IconMainWallet from '@/components/icons/IconMainWallet.vue'
import { useLanguageStore } from '@/stores/languageStore'
import { useUserStore } from '@/stores/userStore'
import BonusItem from './BonusItem.vue'
import WalletItem from './WalletItem.vue'

interface Props {
    data: BonusWalletData
}

const { data } = defineProps<Props>()

const userStore = useUserStore()
const { userInfo } = storeToRefs(userStore)
const languageStore = useLanguageStore()
const { language } = storeToRefs(languageStore)

const formatCurrency = (amount: number) => {
    return amountHelper.getLocaleStringWithSymbol(amount, userInfo.value.currency.toString(), 2)
}

const bonusItemsWithLocalizedTitle = computed(() => {
    return data.bonusItems.map(item => ({
        ...item,
        localizedTitle: item.promotionTitle[language.value.toUpperCase()] ?? item.promotionTitle.EN ?? 'Bonus Wallet',
    }))
})
</script>

<template>
    <div class="bonus-wallet">
        <div class="wallet-section">
            <WalletItem
                type="main"
                :icon="IconMainWallet"
                :label="$t('bw_mainwallet')"
                :amount="formatCurrency(data.mainWalletBalance)"
            />
            <WalletItem
                type="bonus"
                :icon="IconBonusWallet"
                :label="$t('bw_bonuswallet')"
            />
        </div>
        <div class="bonus-list">
            <BonusItem
                v-for="item in bonusItemsWithLocalizedTitle"
                :key="item.id"
                :item="item"
                :formatCurrency="formatCurrency"
                :localizedTitle="item.localizedTitle"
            />
        </div>
    </div>
</template>

<style lang="scss" scoped>
@use "@/assets/styles/common/variables.scss" as *;

.bonus-wallet {
    position: relative;
    display: flex;
    min-width: 270px;
    flex-direction: column;
    align-self: stretch;
    padding: 12px;
    background-image: linear-gradient(to bottom, $indigo-100, $blue-100);
    color: $brandColor-800;
    gap: 8px;

    &::before {
        position: absolute;
        z-index: 0;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-image: url("data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjIwIiBoZWlnaHQ9IjI0OSIgdmlld0JveD0iMCAwIDIyMCAyNDkiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxwYXRoIGQ9Ik02LjU0MTU1IDI0OUg4Ni43NzU5QzkwLjYzODEgMjQ5IDk0LjMxNTYgMjQ3LjI4NCA5Ni43OTI5IDI0NC4zMDFMMTgwLjM3NiAxNDQuMjI1QzE4NS4wMjMgMTM4LjY2IDE4NC4zIDEzMC4zNzQgMTc4Ljc2IDEyNS43MDZMMTQ1LjcyNCA5Ny44NjU2TDIxOC40NTkgMTAuODA1M0MyMjIuMDI5IDYuNTIzMzQgMjE4Ljk5OCAwIDIxMy40NDMgMEwxMzMuMjA5IDBDMTI5LjM0NyAwIDEyNS42NjkgMS43MTU4NiAxMjMuMTkyIDQuNjk5MjhMNTAuMTQ4OCA5Mi4xNDYxQzQ1LjUwMTkgOTcuNzExIDQ2LjIyNTEgMTA1Ljk5NyA1MS43NjQ1IDExMC42NjVMODQuODAwNyAxMzguNTA1TDEuNTQwNzIgMjM4LjE5NUMtMi4wMjkxIDI0Mi40NzcgMS4wMDIxNyAyNDkgNi41NTY5NCAyNDlINi41NDE1NVoiIGZpbGw9InVybCgjcGFpbnQwX2xpbmVhcl84NTgwXzM3MDEpIi8+CjxkZWZzPgo8bGluZWFyR3JhZGllbnQgaWQ9InBhaW50MF9saW5lYXJfODU4MF8zNzAxIiB4MT0iMTUyLjMwOCIgeTE9IjMwLjQyMDgiIHgyPSItMC40MTUyOSIgeTI9IjM1NC4xNSIgZ3JhZGllbnRVbml0cz0idXNlclNwYWNlT25Vc2UiPgo8c3RvcCBzdG9wLWNvbG9yPSIjOTlENUZGIiBzdG9wLW9wYWNpdHk9IjAiLz4KPHN0b3Agb2Zmc2V0PSIxIiBzdG9wLWNvbG9yPSIjOTlENUZGIiBzdG9wLW9wYWNpdHk9IjAuOCIvPgo8L2xpbmVhckdyYWRpZW50Pgo8L2RlZnM+Cjwvc3ZnPgo=");
        background-position: center left 50px;
        background-repeat: no-repeat;
        background-size: 120% 120%;
        content: "";
        pointer-events: none;
    }

    > * {
        position: relative;
        z-index: 1;
    }
}

.wallet-section {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.bonus-list {
    display: flex;
    max-height: 300px;
    flex-direction: column;
    align-self: stretch;
    padding: 4px 8px;
    border-radius: 8px;
    background-image: linear-gradient(to bottom, rgb(255 255 255 / 90%), rgb(255 255 255 / 50%));
    overflow-y: auto;

    &::-webkit-scrollbar {
        width: 4px;
    }

    &::-webkit-scrollbar-thumb {
        border-radius: 2px;
        background-color: #c8c8c8;
    }

    &::-webkit-scrollbar-track {
        border-radius: 2px;
        background-color: #ffffff;
    }
}
</style>
